// MUI Imports
import { lighten } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

// Type Imports
import type { VerticalNavState } from '../../../@menu/contexts/verticalNavContext';
import type { MenuItemStyles } from '../../../@menu/types';

// Util Imports
import { menuClasses } from '../../../@menu/utils/menuClasses';

const menuItemStyles = (verticalNavOptions: VerticalNavState, theme: Theme): MenuItemStyles => {
  // Vars
  const { isCollapsed, isHovered, collapsedWidth, isPopoutWhenCollapsed, transitionDuration } = verticalNavOptions;

  const popoutCollapsed = isPopoutWhenCollapsed && isCollapsed;
  const popoutExpanded = isPopoutWhenCollapsed && !isCollapsed;
  const collapsedNotHovered = isCollapsed && !isHovered;
  return {
    root: ({ level }) => ({
      ...(!isPopoutWhenCollapsed || popoutExpanded || (popoutCollapsed && level === 0)
        ? {
            marginBlockStart: theme.spacing(2.5),
          }
        : {
            marginBlockStart: 0,
          }),
      [`& .${menuClasses.subMenuExpandIcon}`]: {
        '&:hover': {
          color: theme.palette.primary.main,
        },
      },
      [`&.${menuClasses.subMenuRoot}.${menuClasses.open}`]: {
        borderBottom: '1px solid var(--mui-palette-divider)',
        paddingBottom: theme.spacing(1),
      },
      [`& .${menuClasses.button}.active`]: {
        backgroundColor: `${theme.palette.primary.lighterOpacity} !important`,
      },
      [`&.${menuClasses.subMenuRoot} .${menuClasses.subMenuContent} .${menuClasses.button}`]: {
        paddingLeft: '30px',
        '&.active': {
          backgroundColor: 'unset !important',
        },
      },
      [`&.${menuClasses.subMenuRoot}.${menuClasses.open} > .${menuClasses.button}.active, &.${menuClasses.subMenuRoot} > .${menuClasses.button}.active`]:
        {
          border: `1px solid ${theme.palette.primary.main}`,
          [`& .${menuClasses.icon}`]: {
            color: 'var(--primary-color) !important',
          },
        },
      [`&.${menuClasses.disabled} > .${menuClasses.button}`]: {
        color: 'var(--mui-palette-text-disabled)',
        '& *': {
          color: 'inherit',
        },
      },
      [`&:not(.${menuClasses.subMenuRoot}) > .${menuClasses.button}.active`]: {
        ...(popoutCollapsed && level > 0
          ? {
              backgroundColor: theme.palette.primary.lighterOpacity,
              color: 'var(--mui-palette-primary-main)',
              [`& .${menuClasses.icon}`]: {
                color: 'var(--mui-palette-primary-main)',
              },
            }
          : {
              color: 'var(--mui-palette-text-primary)',
              border: `1px solid ${theme.palette.primary.main}`,
              background: theme.palette.background.default,
              [`& .${menuClasses.icon}`]: {
                color: 'var(--primary-color) !important',
              },
            }),
      },
    }),
    button: ({ level, active }) => ({
      paddingBlock: theme.spacing(2),
      ...(!(isCollapsed && !isHovered) && {
        '&:has(.MuiChip-root)': {
          paddingBlock: theme.spacing(1.75),
        },
      }),
      '&.active .ts-menu-label': {
        fontWeight: 500,
      },
      ...((!isPopoutWhenCollapsed || popoutExpanded || (popoutCollapsed && level === 0)) && {
        transition: `all ${transitionDuration}ms ease-in-out`,
        paddingInlineStart: theme.spacing(collapsedNotHovered ? 4.25 : 5.5),
        paddingInlineEnd: theme.spacing(collapsedNotHovered ? 4.25 : 3.5),
        border: '1px solid transparent',
        borderRadius: 8,
      }),

      ...(!active && {
        '&:hover, &:focus-visible': {
          backgroundColor: theme.palette.primary.lighterOpacity,
        },
        '&[aria-expanded="true"]': {
          backgroundColor: theme.palette.primary.lighterOpacity,
        },
      }),
    }),
    icon: ({ level }) => ({
      transition: `margin-inline-end ${transitionDuration}ms ease-in-out`,
      ...(level === 0 && {
        fontSize: '1.375rem',
      }),
      ...(level > 0 && {
        fontSize: '0.75rem',
        color: 'var(--mui-palette-text-secondary)',
      }),
      ...(level === 0 && {
        marginInlineEnd: theme.spacing(2),
      }),
      ...(level > 0 && {
        marginInlineEnd: theme.spacing(3.5),
      }),
      ...(level === 1 &&
        !popoutCollapsed && {
          marginInlineStart: theme.spacing(1.5),
        }),
      ...(level > 1 && {
        marginInlineStart: theme.spacing((popoutCollapsed ? 0 : 1.5) + 2.5 * (level - 1)),
      }),
      ...(collapsedNotHovered && {
        marginInlineEnd: 0,
      }),
      ...(popoutCollapsed &&
        level > 0 && {
          marginInlineEnd: theme.spacing(2),
        }),
      '& > i, & > svg': {
        fontSize: 'inherit',
      },
    }),
    prefix: {
      marginInlineEnd: theme.spacing(2),
    },
    label: ({ level }) => ({
      ...((!isPopoutWhenCollapsed || popoutExpanded || (popoutCollapsed && level === 0)) && {
        transition: `opacity ${transitionDuration}ms ease-in-out`,
        ...(collapsedNotHovered && {
          opacity: 0,
        }),
      }),
    }),
    suffix: {
      marginInlineStart: theme.spacing(2),
    },
    subMenuExpandIcon: {
      fontSize: '1.375rem',
      marginInlineStart: theme.spacing(2),
      '& i, & svg': {
        fontSize: 'inherit',
      },
    },
    subMenuContent: ({ level }) => ({
      zIndex: 'calc(var(--drawer-z-index) + 1)',
      backgroundColor: popoutCollapsed ? 'var(--mui-palette-background-paper)' : 'transparent',
      [`& .${menuClasses.button}.active`]: {
        color: 'var(--primary-color) !important',
        border: '1px solid transparent !important',
      },
      ...(popoutCollapsed &&
        level === 0 && {
          paddingBlock: theme.spacing(2),
          boxShadow: 'var(--mui-customShadows-lg)',
          '[data-skin="bordered"] ~ [data-floating-ui-portal] &': {
            boxShadow: 'none',
            border: '1px solid var(--mui-palette-divider)',
          },
          [`& .${menuClasses.button}`]: {
            paddingInline: theme.spacing(4),
          },
        }),
    }),
  };
};

export default menuItemStyles;
